import {
  Badge,
  Button,
  Checkbox,
  ConfirmSheet,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  Loading,
  PageHeader,
  Select,
  Sheet,
  Surface,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type { Row } from '@/lib/db';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import { ForceClearSheet, TableState } from './TableOccupancy';
import { useOpenSessions, useTableClear } from './tableSupport';
import './tables.css';

type Area = Pick<Row<'area'>, 'id' | 'name' | 'sort' | 'archived_at'>;
type Table = Pick<
  Row<'dining_table'>,
  'id' | 'name' | 'area_id' | 'seats' | 'sort' | 'archived_at'
>;

/**
 * Areas and dining tables for the active location. Study Pods are a separate future module. Every
 * table says whether it is available or occupied (its open session, ADR-021); clearing, ordering
 * and saving need the server now and wait for the connection (ADR-037).
 */
export function TablesPage() {
  const s = useSession();
  const online = useOnline();
  const locationId =
    s.me?.principal === 'device'
      ? (s.me.terminal?.location_id ?? '')
      : (s.me?.active_location_id ?? '');
  const timeZone =
    s.me?.locations.find((l) => l.id === locationId)?.timezone ??
    s.me?.business?.timezone ??
    'Asia/Manila';
  const canManage = s.can('table.manage');
  const canClear = s.can('table.clear');
  const cmd = useCommand([['areas'], ['tables']]);
  // Phase 6: occupancy lives in table sessions and ends only with CLEAR TABLE (ADR-021).
  const sessions = useOpenSessions(locationId);
  const tableClear = useTableClear(
    s.me?.principal === 'human' ? locationId : null,
    s.can('table.force_clear'),
  );
  const sessionOf = (tableId: string) =>
    (sessions.data ?? []).find((x) => x.dining_table_id === tableId);
  const clockTime = (iso: string) =>
    new Intl.DateTimeFormat('en-PH', { timeZone, hour: 'numeric', minute: '2-digit' }).format(
      new Date(iso),
    );
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Table | 'new' | null>(null);
  const [editingArea, setEditingArea] = useState<Area | 'new' | null>(null);

  const areas = useQuery({
    queryKey: ['areas', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('area')
        .select('id, name, sort, archived_at')
        .eq('location_id', locationId)
        .order('sort');
      if (error) throw error;
      return data;
    },
  });
  const tables = useQuery({
    queryKey: ['tables', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dining_table')
        .select('id, name, area_id, seats, sort, archived_at')
        .eq('location_id', locationId)
        .order('sort');
      if (error) throw error;
      return data;
    },
  });

  const liveAreas = (areas.data ?? []).filter((a) => !a.archived_at);
  const visibleTables = (tables.data ?? []).filter((t) => showArchived || !t.archived_at);
  const areaName = (id: string | null) => liveAreas.find((a) => a.id === id)?.name ?? '—';

  const move = async (t: Table, dir: -1 | 1) => {
    const live = (tables.data ?? []).filter((x) => !x.archived_at);
    const i = live.findIndex((x) => x.id === t.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= live.length) return;
    const ids = live.map((x) => x.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    await cmd.mutateAsync({
      kind: 'table.reorder',
      payload: { location_id: locationId, ordered_ids: ids },
    });
  };

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Floor"
        title="Tables"
        subtitle="The café's tables, grouped by area. A dine-in order is taken at a table."
        actions={
          canManage ? (
            <>
              <Button variant="ghost" onClick={() => setEditingArea('new')} data-testid="new-area">
                New area
              </Button>
              <Button variant="primary" onClick={() => setEditing('new')} data-testid="new-table">
                New table
              </Button>
            </>
          ) : undefined
        }
      />

      <Surface pad="sm">
        <div className="d-spread">
          <div className="tables-areas">
            <span className="d-eyebrow">Areas</span>
            {areas.data && liveAreas.length === 0 && <span className="d-muted">No areas yet.</span>}
            {liveAreas.map((a) =>
              canManage ? (
                <Button
                  key={a.id}
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingArea(a)}
                  data-testid={`area-${a.id}`}
                >
                  {a.name}
                </Button>
              ) : (
                <Badge key={a.id} tone="accent" testId={`area-${a.id}`}>
                  {a.name}
                </Badge>
              ),
            )}
          </div>
          <Checkbox checked={showArchived} onChange={setShowArchived} label="Show archived" />
        </div>
      </Surface>
      {areas.error && (
        <ErrorState
          title="Could not load the areas"
          message={errorText(areas.error)}
          onRetry={() => void areas.refetch()}
        />
      )}
      {tables.error && (
        <ErrorState
          title="Could not load the tables"
          message={errorText(tables.error)}
          onRetry={() => void tables.refetch()}
        />
      )}

      <div className="d-list" data-testid="table-list">
        {tables.isLoading && <Loading rows={3} label="Loading the tables" />}
        {visibleTables.map((t) => {
          const session = sessionOf(t.id);
          const clear = canClear && !!session && !t.archived_at;
          const actions = clear || canManage;
          return (
            <div
              key={t.id}
              className={`d-row tables-row${session ? ' tables-row--occupied' : ''}${t.archived_at ? ' tables-row--archived' : ''}`}
              data-testid={`table-row-${t.id}`}
            >
              <div className="d-row__main">
                <div className="d-row__title tables-row__title">
                  <span className="tables-row__name">{t.name}</span>
                  {t.archived_at && <Badge>archived</Badge>}
                  {(session || !t.archived_at) && <TableState occupied={!!session} />}
                </div>
                <div className="d-row__meta">
                  {areaName(t.area_id)}
                  {t.seats ? ` · ${t.seats} seats` : ''}
                  {session ? ` · occupied since ${clockTime(session.opened_at)}` : ''}
                </div>
              </div>
              {actions && (
                <div className="d-actions">
                  {clear && (
                    <Button
                      size="sm"
                      variant="danger"
                      busy={tableClear.busy === t.id}
                      disabled={!online}
                      onClick={() => void tableClear.clear({ id: t.id, name: t.name })}
                      data-testid={`clear-${t.name}`}
                    >
                      Clear table
                    </Button>
                  )}
                  {canManage && !t.archived_at && (
                    <>
                      <IconButton
                        size="sm"
                        variant="quiet"
                        label={`move ${t.name} up`}
                        disabled={!online}
                        onClick={() => void move(t, -1)}
                      >
                        ↑
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="quiet"
                        label={`move ${t.name} down`}
                        disabled={!online}
                        onClick={() => void move(t, 1)}
                      >
                        ↓
                      </IconButton>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(t)}
                        data-testid={`edit-table-${t.name}`}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                  {canManage && t.archived_at && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!online}
                      onClick={() =>
                        void cmd.mutateAsync({ kind: 'table.restore', payload: { table_id: t.id } })
                      }
                    >
                      Restore
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {tables.data && visibleTables.length === 0 && (
          <EmptyState title="No tables yet">Create the first one.</EmptyState>
        )}
      </div>

      <ForceClearSheet
        request={tableClear.force}
        busy={!!tableClear.busy}
        onConfirm={(why) => void tableClear.forceClear(why)}
        onClose={tableClear.cancel}
      />
      {editing && (
        <TableSheet
          table={editing === 'new' ? null : editing}
          areas={liveAreas}
          onClose={() => setEditing(null)}
          onSave={async (values) => {
            if (editing === 'new')
              await cmd.mutateAsync({
                kind: 'table.create',
                payload: { location_id: locationId, ...values },
              });
            else
              await cmd.mutateAsync({
                kind: 'table.update',
                payload: { table_id: editing.id, ...values },
              });
            setEditing(null);
          }}
          onArchive={
            editing !== 'new'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'table.archive',
                    payload: { table_id: editing.id },
                  });
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
      {editingArea && (
        <AreaSheet
          area={editingArea === 'new' ? null : editingArea}
          onClose={() => setEditingArea(null)}
          onSave={async (name) => {
            if (editingArea === 'new')
              await cmd.mutateAsync({
                kind: 'area.create',
                payload: { location_id: locationId, name },
              });
            else
              await cmd.mutateAsync({
                kind: 'area.update',
                payload: { area_id: editingArea.id, name },
              });
            setEditingArea(null);
          }}
          onArchive={
            editingArea !== 'new'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'area.archive',
                    payload: { area_id: editingArea.id },
                  });
                  setEditingArea(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function TableSheet({
  table,
  areas,
  onClose,
  onSave,
  onArchive,
}: {
  table: Table | null;
  areas: Area[];
  onClose: () => void;
  onSave: (v: { name: string; area_id: string | null; seats: number | null }) => Promise<void>;
  onArchive?: () => Promise<void>;
}) {
  const online = useOnline();
  const [name, setName] = useState(table?.name ?? '');
  const [areaId, setAreaId] = useState(table?.area_id ?? '');
  const [seats, setSeats] = useState(table?.seats?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        name: name.trim(),
        area_id: areaId || null,
        seats: seats ? Number(seats) : null,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title={table ? `Edit ${table.name}` : 'New table'}>
      <form onSubmit={(e) => void submit(e)} className="d-stack">
        <Field label="Name" help="Short and visible from the counter, e.g. T1, Outdoor 2">
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={30}
              autoFocus
              data-testid="table-name"
            />
          )}
        </Field>
        <Field label="Area">
          {(id) => (
            <Select
              id={id}
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              data-testid="table-area"
            >
              <option value="">No area</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Seats (optional)">
          {(id) => (
            <Input
              id={id}
              type="number"
              min={1}
              max={100}
              inputMode="numeric"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              data-testid="table-seats"
            />
          )}
        </Field>
        <div className="tables-sheet__actions">
          {onArchive && (
            <Button
              variant="danger"
              className="tables-sheet__archive"
              disabled={!online}
              onClick={() => void onArchive()}
              data-testid="archive-table"
            >
              Archive
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!online}
            data-testid="save-table"
          >
            Save
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

function AreaSheet({
  area,
  onClose,
  onSave,
  onArchive,
}: {
  area: Area | null;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  onArchive?: () => Promise<void>;
}) {
  const online = useOnline();
  const [name, setName] = useState(area?.name ?? '');
  const [busy, setBusy] = useState(false);
  // there is no way back from archiving an area (no restore), so it asks first
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  return (
    <Sheet open onClose={onClose} title={area ? `Edit ${area.name}` : 'New area'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void onSave(name.trim()).finally(() => setBusy(false));
        }}
        className="d-stack"
      >
        <Field label="Name" help="e.g. Main Dining, Outdoor, Study Area">
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={60}
              autoFocus
              data-testid="area-name"
            />
          )}
        </Field>
        <div className="tables-sheet__actions">
          {onArchive && (
            <Button
              variant="danger"
              className="tables-sheet__archive"
              disabled={!online}
              onClick={() => setConfirmArchive(true)}
            >
              Archive
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!online}
            data-testid="save-area"
          >
            Save
          </Button>
        </div>
      </form>
      {area && onArchive && (
        <ConfirmSheet
          open={confirmArchive}
          title={`Archive ${area.name}`}
          confirmLabel="Archive area"
          busy={archiving}
          onClose={() => setConfirmArchive(false)}
          onConfirm={() => {
            setArchiving(true);
            void onArchive().finally(() => setArchiving(false));
          }}
        >
          <p>
            {area.name} leaves the list of areas and cannot be brought back from here. Its tables
            stay, with no area.
          </p>
        </ConfirmSheet>
      )}
    </Sheet>
  );
}
